import React from 'react';
import { Loader2 } from 'lucide-react';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'constructive' | 'success';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

// Darker shade shown as the "side face" behind the lifted front
const edgeColors: Record<Exclude<Variant, 'ghost'>, string> = {
  primary:      '#991b1b',
  secondary:    'var(--btn-secondary-edge)',
  danger:       '#7f1d1d',
  constructive: '#0369a1',
  success:      '#15803d',
};

const frontClassMap: Record<Exclude<Variant, 'ghost'>, string> = {
  primary:      styles['front-primary'],
  secondary:    styles['front-secondary'],
  danger:       styles['front-danger'],
  constructive: styles['front-constructive'],
  success:      styles['front-success'],
};

const radiusClassMap: Record<Size, string> = {
  sm: styles['size-sm'],
  md: styles['size-md'],
  lg: styles['size-lg'],
};

const paddingClassMap: Record<Size, string> = {
  sm: styles['padding-sm'],
  md: styles['padding-md'],
  lg: styles['padding-lg'],
};

const focusClassMap: Record<Variant, string> = {
  primary:      '',
  secondary:    '',
  ghost:        '',
  danger:       styles['btn-danger'],
  constructive: styles['btn-constructive'],
  success:      styles['btn-success'],
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  // Ghost is flat — no 3D depth
  if (variant === 'ghost') {
    return (
      <button
        className={[
          styles.ghost,
          radiusClassMap[size],
          paddingClassMap[size],
          className,
        ].filter(Boolean).join(' ')}
        disabled={isDisabled}
        {...props}
      >
        {loading && <Loader2 className={styles.spinner} />}
        {children}
      </button>
    );
  }

  return (
    <button
      className={[
        styles.btn,
        radiusClassMap[size],
        focusClassMap[variant],
        fullWidth ? styles['full-width'] : '',
        isDisabled ? styles.disabled : '',
        className,
      ].filter(Boolean).join(' ')}
      disabled={isDisabled}
      {...props}
    >
      <span className={styles.shadow} />
      <span
        className={styles.edge}
        style={{ background: edgeColors[variant as Exclude<Variant, 'ghost'>] }}
      />
      <span
        className={[
          styles.front,
          frontClassMap[variant as Exclude<Variant, 'ghost'>],
          paddingClassMap[size],
          radiusClassMap[size],
        ].join(' ')}
      >
        {loading && <Loader2 className={styles.spinner} />}
        {children}
      </span>
    </button>
  );
}
